import React, { Component } from 'react';
import { styled } from '@mui/material/styles';
import { isEqual, cloneDeep } from 'lodash';
import { withFormsy } from 'formsy-react';
import FileChip from './FileChip';
import { DropzoneDialog } from 'react-mui-dropzone';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import PropTypes from 'prop-types';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { getAccessToken } from 'auth';
import { API_URL } from 'atlasConfig';
import generateNonce from 'utils/generateNonce';
import clsx from 'clsx';
import pluralize from 'pluralize';

const PREFIX = 'FileUploadOnSelectFormsy';

const classes = {
  root: `${PREFIX}-root`,
  chips: `${PREFIX}-chips`,
  existing: `${PREFIX}-existing`,
  selected: `${PREFIX}-selected`,
};

const Root = styled('div')({
  [`&.${classes.root}`]: {
    '& > label': {
      marginBottom: 15,
    },
    '& > button': {
      marginRight: 15,
    },
  },
  [`& .${classes.chips}`]: {
    display: 'inline',
    '& > *': {
      marginRight: 5,
    },
  },
  [`& .${classes.existing}`]: {
    // existing file chips
  },
  [`& .${classes.selected}`]: {
    // selected file chips
  },
});

class FileUploader extends Component {
  #mounted = false;

  constructor(props) {
    super(props);
    this.state = {
      open: false,
      files: {},
    };
  }

  componentDidMount() {
    this.#mounted = true;
  }

  componentWillUnmount() {
    this.#mounted = false;
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.existingFiles, this.props.existingFiles)) {
      this.setState({
        open: false,
        files: {},
      });
    }
  }

  handleClose = () => {
    this.setState({
      open: false,
    });
  };

  handleOpen = () => {
    if (this.props.disabled) return;
    this.setState({
      open: true,
    });
  };

  readFile(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        resolve(event?.target?.result);
      };
      reader.onerror = (event) => {
        reader.abort();
        reject(event);
      };
      reader.readAsDataURL(file);
    });
  }

  generateCompletedFiles = (sectionUpdateResponse) => {
    const { name, existingFiles } = this.props;
    const existingIds = existingFiles.map((f) => f.id);

    const uploaded = JSON.parse(sectionUpdateResponse).responses.filter(
      (res) => res.questionAlias === this.props.name && !existingIds.includes(res.id),
    );
    const newFileObjs = {};
    uploaded.map((f) => {
      newFileObjs[f.id] = {
        status: 'completed',
        percentComplete: 1,
        file: {
          id: f.id,
          name: f.fileName,
          extention: f.extention,
        },
      };
    });
    return newFileObjs;
  };

  async doUpload(uuid) {
    const file = this.state.files[uuid].file;
    const { onGeneratePostFileData, name } = this.props;
    if (!onGeneratePostFileData) {
      alert('Do not know how to save file to server?');
      return;
    }
    const token = await getAccessToken();
    const data = (await this.readFile(file)).split(',')[1];
    const fileName = file.name || '';
    const extension = fileName.split('.').pop() || '';
    const body = onGeneratePostFileData(name, data, fileName, extension);
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.addEventListener('load', () => {
        const { onUpdateSection } = this.props;
        if (this.#mounted && xhr.readyState == XMLHttpRequest.DONE) {
          if (xhr.status === 200) {
            const response = JSON.parse(xhr.responseText);
            onUpdateSection && onUpdateSection(response);
          } else if (xhr.status >= 400) {
            // in development api will throw exceptions, error text
            // in production api will return ErrorDto, json format
            let message = 'Uploading failed';
            try {
              message = JSON.parse(xhr.responseText).message;
            } catch (e) {
              console.error(e); // error in the above string (in this case, yes)!
            }
            this.setState({
              files: {
                ...this.state.files,
                [uuid]: {
                  file: file,
                  error: message,
                  status: 'failed',
                  percentComplete: 0,
                },
              },
            });
          }
        }
        resolve();
      });
      xhr.addEventListener('error', () => {
        if (this.#mounted) {
          this.setState({
            files: {
              ...this.state.files,
              [uuid]: {
                file: file,
                error: 'Uploading failed',
                status: 'failed',
                percentComplete: 0,
              },
            },
          });
        }
        resolve();
      });

      const url = `${API_URL}/api/Questionnaire/File`;
      xhr.open('POST', url);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Authorization', 'Bearer ' + token.accessToken);
      // TODO: make locale dynamic
      xhr.setRequestHeader('Accept-Language', 'en-GB');
      xhr.send(JSON.stringify(body));
    });
  }

  handleSave = (files) => {
    const newFileObjects = {};
    files.map((file) => {
      newFileObjects[generateNonce()] = {
        status: 'pending',
        file: file,
        percentComplete: 0.25,
      };
    });

    this.setState(
      {
        files: {
          ...this.state.files,
          ...newFileObjects,
        },
        open: false,
      },
      () => {
        Promise.all(Object.keys(newFileObjects).map((fileUuid) => this.doUpload(fileUuid)));
      },
    );
  };

  handleDelete = (id) => {
    const { disabled, onDeleteFile } = this.props;
    const { files } = this.state;
    const file = files[id];
    if (disabled) return;
    if (file) {
      const newFiles = { ...files };
      delete newFiles[id];
      this.setState({ files: newFiles });
    }
    if (file?.status !== 'failed') {
      onDeleteFile && onDeleteFile(id);
    }
  };

  renderExistingFileChips = () => {
    const { existingFiles: files } = this.props;
    if (!files || files.length === 0) {
      return <span></span>;
    }
    if (Array.isArray(files)) {
      return files.map((file, i) => (
        <FileChip key={file.id} file={file} onDelete={this.handleDelete} />
      ));
    } else {
      return <></>;
    }
  };

  renderSelectedFileChips = () => {
    const { existingFiles } = this.props;
    const { files } = this.state;
    if (files.length === 0 && (!existingFiles || existingFiles.length === 0)) {
      return <span>No File</span>;
    }
    return Object.keys(files).map((uuid) => {
      const fileObj = files[uuid];
      return <FileChip key={uuid} id={uuid} fileObj={fileObj} onDelete={this.handleDelete} />;
    });
  };

  render() {
    const {
      extensions = [],
      label,
      name,
      disabled,
      description,
      filesMax,
      maxFileSize = 25000,
      existingFiles,
    } = this.props;
    const itemsLength = existingFiles?.length ?? 0;
    const newlyAdded = Object.keys(this.state.files).length ?? 0;
    const available = filesMax - itemsLength - newlyAdded;
    const allowOpen = available > 0 && !disabled;
    const acceptedFiles = extensions.map((ext) => `.${ext}`);
    return (
      <Root className={classes.root}>
        <p>{label}</p>
        <Button
          variant="contained"
          color="primary"
          onClick={allowOpen ? this.handleOpen : null}
          disabled={!allowOpen}
          startIcon={<CloudUploadIcon />}
        >
          {allowOpen ? 'Upload' : 'Upload Disabled'}
        </Button>
        <div className={classes.chips}>
          {this.renderExistingFileChips()}
          {this.renderSelectedFileChips()}
        </div>
        <DropzoneDialog
          filesLimit={available}
          maxFileSize={maxFileSize * 1024} // number in KB
          cancelButtonText="Cancel"
          submitButtonText="Save"
          open={this.state.open}
          onSave={this.handleSave}
          acceptedFiles={acceptedFiles}
          showPreviews={true}
          onClose={this.handleClose}
        />
        <FormHelperText id="component-helper-text-file-types">{`Allowed file types: ${extensions.join(', ')}`}</FormHelperText>
        {description && <FormHelperText id="component-helper-text">{description}</FormHelperText>}
      </Root>
    );
  }
}

FileUploader.propTypes = {
  extensions: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  filesMax: PropTypes.number,
  filesMin: PropTypes.number,
  maxFileSize: PropTypes.number,
  label: PropTypes.string,
  description: PropTypes.string,
  setValue: PropTypes.func,
  classes: PropTypes.object,
  existingFiles: PropTypes.arrayOf(PropTypes.object),
  onDeleteFile: PropTypes.func,
  onUpdateSection: PropTypes.func,
  onGeneratePostFileData: PropTypes.func,
};

const StyledFileUploader = FileUploader;

export default React.memo(withFormsy(StyledFileUploader));
